/// <amd-module name="Repos/VersionDialogs/Components/Create/CreateDialog" />
import * as React from "react";
import { GitRepository, GitVersionDescriptor, GitVersionOptions, GitVersionType } from "Repos/Common/Contracts/Git";
import { refToBranchName } from "Repos/Common/Util/Ref";
import { RepositoryDropdown } from "Repos/RepoDropdown/Components/RepositoryDropdown";
import { IRepositoryDropdownItem } from "Repos/RepoDropdown/RepositoryDropdownProvider";
import * as Resources from "Repos/VersionDialogs/Resources";
import { VersionDropdown, VersionDropdownExpandableTextField } from "Repos/VersionDropdown/Components/VersionDropdown";
import { IVersionDropdownItem } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IButtonProps } from "VSSUI/Button";
import { CustomDialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { PanelContent, PanelFooter, PanelHeader } from "VSSUI/Panel";
import { TextField } from "VSSUI/TextField";
import { KeyCode } from "VSSUI/Util";
import { DialogResult } from "../../VersionDialogsService";
import { ICreateDialogProps } from "./CreateDialog.Props";

const enum ValidationKind {
    None = 0,
    Name = 1,
    Description = 2,
    CreateButton = 4,
    NameExists = 8
}

const dialogTitleId = "create-version-dialog";

export class CreateDialog extends React.Component<ICreateDialogProps> {
    private canCreate: IObservableValue<boolean>;
    private descriptionField: IObservableValue<string>;
    private descriptionFieldError: IObservableValue<boolean>;
    private nameField: IObservableValue<string>;
    private nameFieldError: IObservableValue<boolean>;
    private nameFieldMessage: IObservableValue<string | undefined>;
    private initialRepository?: GitRepository;
    private selectedRepository: IObservableValue<GitRepository | undefined>;
    private referenceField: IObservableValue<GitVersionDescriptor | undefined>;
    private referenceFieldError: IObservableValue<boolean>;
    private referenceFieldMessage: IObservableValue<string | undefined>;
    private selectedRepositoryId?: string;
    private repositories?: GitRepository[];
    private nameSuggestion: IObservableValue<string>;

    private timerManagement = new TimerManagement();

    private validateDebounced: typeof this.validate;

    constructor(props: ICreateDialogProps) {
        super(props);

        this.canCreate = new ObservableValue(false);
        this.descriptionField = new ObservableValue("");
        this.descriptionFieldError = new ObservableValue(false);
        this.nameField = new ObservableValue(this.props.initialName || "");
        this.nameFieldError = new ObservableValue(false);
        this.nameFieldMessage = new ObservableValue(undefined);
        this.initialRepository = this.props.repository;
        this.selectedRepository = new ObservableValue(this.initialRepository);
        this.referenceField = new ObservableValue(this.getDefaultReference(this.initialRepository));
        this.referenceFieldError = new ObservableValue(false);
        this.referenceFieldMessage = new ObservableValue(undefined);
        this.selectedRepositoryId = this.props.repository?.id;
        this.nameSuggestion = new ObservableValue("");

        // Do not validate name or description until user takes an action
        this.validate(ValidationKind.CreateButton);

        // We throttle validations from user input
        this.validateDebounced = this.timerManagement.debounce(this.validate, 500);

        this.suggestName(this.initialRepository);
    }

    public render(): JSX.Element {
        const { createResult, dialogTitle, linkingRegion, onCreateComplete, onDismiss, showDescription } = this.props;
        return (
            <CustomDialog
                ariaLabelledBy={dialogTitleId}
                defaultActiveElement=".item-name-input"
                lightDismiss={false}
                modal={true}
                onDismiss={onDismiss}
            >
                <PanelHeader onDismiss={onDismiss} showCloseButton={false} titleProps={{ text: dialogTitle, id: dialogTitleId }} />
                <PanelContent className="bolt-dialog-content">
                    <div className="flex-column rhythm-vertical-16 flex-grow padding-4 scroll-auto">
                        <Observer createResult={createResult}>
                            {(props: { createResult: DialogResult }) => {
                                const { error } = props.createResult;
                                return error ? <MessageCard severity={MessageCardSeverity.Error}>{error.message}</MessageCard> : null;
                            }}
                        </Observer>

                        <FormItem
                            ariaLabel={Resources.NameAriaLabel}
                            label={Resources.NameLabel}
                            error={this.nameFieldError}
                            message={this.nameFieldMessage}
                        >
                            <TextField
                                inputClassName="item-name-input"
                                onChange={this.onNameChange}
                                onKeyDown={this.onKeyDown}
                                value={this.nameField}
                            />
                            <Observer nameSuggestion={this.nameSuggestion}>
                                {({ nameSuggestion }) => {
                                    return nameSuggestion && <span className="suggestion-text">{Resources.SuggestionLabel}
                                        <a className="text-underlined cursor-pointer" onClick={() => {
                                            this.nameField.value = nameSuggestion;
                                            this.validateDebounced(ValidationKind.CreateButton | ValidationKind.Name | ValidationKind.NameExists);
                                        }}>{nameSuggestion}</a></span>
                                }}
                            </Observer>
                        </FormItem>

                        {this.props.allowChangingRepository && (
                            <FormItem label={Resources.RepositoryLabel}>
                                <RepositoryDropdown
                                    className="create-dialog-repository-dropdown"
                                    providerOptions={{}}
                                    selectedRepositoryItem={this.initialRepository}
                                    onSelect={this.onRepositoryChange}
                                    onRepositoriesLoaded={this.onRepositoriesLoaded}
                                />
                            </FormItem>
                        )}

                        <FormItem label={Resources.BasedOnLabel} error={this.referenceFieldError} message={this.referenceFieldMessage}>
                            <Observer repository={this.selectedRepository} reference={this.referenceField}>
                                {({ repository, reference }) => (
                                    <VersionDropdown
                                        key={`versions-for-repository-${repository?.id}`}
                                        disabled={!repository}
                                        isDropdownFullWidth={true}
                                        initiallySelectedVersion={reference}
                                        onSelect={this.onReferenceChange}
                                        providerOptions={{ repository, viewCommitsPivot: true }}
                                        renderExpandable={VersionDropdownExpandableTextField}
                                    />
                                )}
                            </Observer>
                        </FormItem>

                        {showDescription && (
                            <FormItem label={Resources.DescriptionLabel} error={this.descriptionFieldError}>
                                <TextField multiline={true} onChange={this.onDescriptionChange} rows={3} value={this.descriptionField} />
                            </FormItem>
                        )}

                        {linkingRegion && linkingRegion()}
                    </div>
                </PanelContent>
                <Observer canCreate={this.canCreate} createResult={createResult}>
                    {(props: { canCreate: boolean; createResult: DialogResult }) => {
                        let inProgress: boolean | undefined;
                        if (props.createResult.item) {
                            // We still want to keep buttons disabled until dialog is disposed
                            inProgress = true;
                        } else {
                            inProgress = props.createResult.inProgress;
                        }

                        const dialogFooterProps: ICreateDialogFooterProps = {
                            buttonProps: [
                                {
                                    disabled: inProgress,
                                    id: "create-dialog-cancel",
                                    onClick: onDismiss,
                                    text: Resources.CancelButtonText
                                },
                                {
                                    disabled: !props.canCreate || inProgress,
                                    id: "create-dialog-confirm",
                                    onClick: this.onCreateClick,
                                    primary: true,
                                    text: Resources.CreateButtonText
                                }
                            ],
                            createResult: props.createResult,
                            onCreateComplete
                        };

                        return <CreateDialogFooter {...dialogFooterProps} />;
                    }}
                </Observer>
            </CustomDialog>
        );
    }

    public componentDidUpdate(prevProps: ICreateDialogProps): void {
        if (this.props.repository !== prevProps.repository) {
            this.initialRepository = this.props.repository;
            this.selectedRepositoryId = this.props.repository?.id;
            this.selectRepository(this.initialRepository);
        }
    }

    private onRepositoriesLoaded = (repositories: GitRepository[]): void => {
        this.repositories = repositories;
        if (!this.selectedRepository.value && this.selectedRepositoryId) {
            const selectedRepository = repositories.find(r => r.id === this.selectedRepositoryId);
            if (selectedRepository) {
                this.selectRepository(selectedRepository);
            }
        }
    };

    private getDefaultReference = (repository?: GitRepository | undefined): GitVersionDescriptor | undefined => {
        // Try to set the default branch if there is no reference version specified
        if (repository?.id === this.initialRepository?.id && this.props.initialReferenceVersion) {
            return this.props.initialReferenceVersion;
        }

        if (repository) {
            return {
                version: refToBranchName(repository.defaultBranch),
                versionOptions: GitVersionOptions.None,
                versionType: GitVersionType.Branch
            };
        }

        return undefined;
    };
    private validate = (validationKind: ValidationKind): void => {
        const { getRefByName, nameExistsMessage, onValidateName, showDescription } = this.props;

        const name = this.nameField.value.trim();

        const nameValidationMessage = onValidateName(name);
        const nameIsValid = nameValidationMessage === undefined;
        if ((validationKind & ValidationKind.Name) > 0) {
            this.nameFieldError.value = !nameIsValid;
            this.nameFieldMessage.value = nameValidationMessage;
        }

        if (nameIsValid && (validationKind & ValidationKind.NameExists) > 0) {
            this.nameFieldError.value = false;
            this.nameFieldMessage.value = "";

            getRefByName(name, this.selectedRepository.value).then(ref => {
                if (name === this.nameField.value.trim() && ref) {
                    this.nameFieldError.value = true;
                    this.nameFieldMessage.value = nameExistsMessage;
                    this.canCreate.value = false;
                }
            });
        }

        const referenceVersionIsValid = !!this.referenceField.value?.version;
        this.referenceFieldError.value = !referenceVersionIsValid;
        this.referenceFieldMessage.value = referenceVersionIsValid ? "" : Resources.BasedOnError;

        const descriptionIsValid = !showDescription || !!this.descriptionField.value.trim();
        if ((validationKind & ValidationKind.Description) > 0) {
            this.descriptionFieldError.value = !descriptionIsValid;
        }

        const repositoryIsValid = !!this.selectedRepository.value;

        if ((validationKind & ValidationKind.CreateButton) > 0) {
            this.canCreate.value = nameIsValid && descriptionIsValid && referenceVersionIsValid && repositoryIsValid;
        }
    };

    private suggestName = (repository?: GitRepository | undefined): void => {
        this.nameSuggestion.value = "";
        if (this.props.getNameSuggestion && repository) {
            this.props.getNameSuggestion(repository).then(suggestion => {
                if (suggestion)
                    this.nameSuggestion.value = suggestion;
            });
        }
    };

    private create = (): void => {
        const { onCreateClick } = this.props;
        const name = this.nameField.value.trim();
        const versionDescription = this.descriptionField.value.trim();
        const reference = this.referenceField.value!;
        const repositoryItem = this.selectedRepository.value!;

        onCreateClick(name, versionDescription, reference, repositoryItem);
    };

    private onCreateClick = (): void => {
        this.create();
    };

    private onNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        this.nameField.value = value;
        this.validateDebounced(ValidationKind.CreateButton | ValidationKind.Name | ValidationKind.NameExists);
    };

    private onDescriptionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        this.descriptionField.value = value;
        this.validateDebounced(ValidationKind.CreateButton | ValidationKind.Description);
    };

    private onReferenceChange = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem>): void => {
        if (item.data) {
            this.referenceField.value = item.data.versionDescriptor;
        }
    };

    private onRepositoryChange = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IRepositoryDropdownItem>): void => {
        if (!item.data) {
            return;
        }

        this.selectedRepositoryId = item.data.id;
        const repository = item.data.repository ?? this.repositories?.find(r => r.id === item.data!.id); // Favorite repos never pass repo info
        this.selectRepository(repository);
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (event.keyCode === KeyCode.enter && this.canCreate.value) {
            this.create();
        }
    };

    private selectRepository = (repository: GitRepository | undefined): void => {
        this.selectedRepository.value = repository;
        this.referenceField.value = this.getDefaultReference(repository);
        this.validate(ValidationKind.CreateButton | ValidationKind.NameExists);
        if (repository) {
            this.suggestName(repository);
        }
    };
}

interface ICreateDialogFooterProps {
    buttonProps: IButtonProps[];
    createResult: DialogResult;
    onCreateComplete: (item: any) => void;
}

class CreateDialogFooter extends React.Component<ICreateDialogFooterProps> {
    public render(): JSX.Element {
        const { buttonProps } = this.props;
        return <PanelFooter buttonProps={buttonProps} />;
    }

    public componentDidUpdate(): void {
        const { createResult, onCreateComplete } = this.props;
        if (createResult.item) {
            onCreateComplete(createResult.item);
        }
    }
}
